import { useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { cancelRefresh, scheduleRefresh } from './tokenRefresh';
import type { TokenRefreshDependencies } from './tokenRefresh';
import { hostBridge } from '../messaging/hostBridge';
import { isInboundMessage } from '../messaging/protocol';
import { isOriginAllowed } from '../messaging/originAllowlist';
import { waitForInFlightSync } from '@src/config/syncCoordinator';
import { resetClient } from '@src/api/TimePunchClockApi';
import { useNetworkStatus, reportNetworkError } from '@src/context/NetworkStatusContext';

// Bounds how long teardown waits for an in-flight batch sync (API-4379)
// before proceeding — long enough to let a typical batch request settle,
// short enough that a hung request can't block `7punches:destroy` forever.
const DESTROY_SYNC_WAIT_MS = 3000;

export function useTokenRefresh() {
    const {
        accessToken,
        refreshToken,
        expiresAt,
        mode,
        setTokens,
        clearAuth,
        markSessionExpired
    } = useAuth();
    const { isOnline } = useNetworkStatus();

    // Keep mutable refs so the timer callbacks always read fresh state without
    // needing to re-schedule on every state change.
    const refreshTokenRef = useRef(refreshToken);
    const modeRef = useRef(mode);
    refreshTokenRef.current = refreshToken;
    modeRef.current = mode;

    // Set when a refresh is deferred because its fetch failed offline (see
    // tokenRefresh's network-failure branch). Signals that the next confirmed
    // reconnect must re-attempt the refresh immediately rather than waiting for
    // the normal cadence (API-4800).
    const deferredRef = useRef(false);

    useEffect(() => {
        if (!accessToken || !expiresAt || !refreshToken) {
            cancelRefresh();
            return;
        }

        // Never arm a refresh timer while offline — the fetch would only fail
        // and re-defer, hammering a dead connection. Wait for
        // NetworkStatusContext to confirm reconnection, which re-runs this
        // effect with `isOnline === true`.
        if (!isOnline) {
            cancelRefresh();
            return;
        }

        const deps = buildDeps();

        if (deferredRef.current) {
            // A previous refresh was deferred while offline. Connectivity is
            // now confirmed, so re-attempt immediately — the access token may
            // still have life left, so the normal 75%-of-remaining path would
            // wrongly delay the retry (API-4800).
            deferredRef.current = false;
            scheduleRefresh(0, deps);
        } else {
            const remainingMs = new Date(expiresAt).getTime() - Date.now();
            const remainingSeconds = Math.max(remainingMs / 1000, 0);
            scheduleRefresh(remainingSeconds, deps);
        }

        return () => {
            cancelRefresh();
        };

        function buildDeps(): TokenRefreshDependencies {
            return {
                getRefreshToken: () => refreshTokenRef.current,
                getMode: () => modeRef.current,
                setTokens,
                clearAuth,
                onSessionExpired: () => {
                    hostBridge.sessionExpired();
                    markSessionExpired();
                },
                onNetworkDeferred: () => {
                    // Remember the pending refresh so the next confirmed
                    // reconnect re-attempts it, and push the app into offline
                    // mode so NetworkStatusContext's reconnect flow flips
                    // `isOnline` back to true and re-runs this effect.
                    deferredRef.current = true;
                    reportNetworkError();
                }
            };
        }
        // Re-schedule when the token identity/expiry changes, or when
        // `isOnline` flips back to true. A network-deferred refresh (see
        // tokenRefresh's `isNetworkFailure` branch) reports the failure via
        // `onNetworkDeferred`, which flips the app offline; the confirmed
        // reconnect then flips `isOnline` back to true, re-running this effect
        // with `deferredRef` set so the refresh is retried immediately.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, expiresAt, isOnline]);

    useEffect(() => {
        let isMounted = true;

        function handleMessage(event: MessageEvent) {
            if (!isOriginAllowed(event.origin)) return;
            const data = event.data;
            if (!isInboundMessage(data)) return;

            if (data.type === '7punches:destroy') {
                void (async () => {
                    // Give an in-flight batch sync a chance to finish so a
                    // punch stored right before teardown isn't stranded
                    // behind a cleared session (bounded — never blocks
                    // teardown indefinitely).
                    await waitForInFlightSync(DESTROY_SYNC_WAIT_MS);
                    if (!isMounted) return;
                    cancelRefresh();
                    // Release the Koin graph on session end (API-4681) so a
                    // fresh launch/init after this always starts clean.
                    resetClient();
                    clearAuth();
                })();
            }
        }

        window.addEventListener('message', handleMessage);
        return () => {
            isMounted = false;
            window.removeEventListener('message', handleMessage);
        };
    }, [clearAuth]);
}
